import * as React from 'react';
import { toast } from 'sonner';
import { ASSET_STATUS_LABELS, canTransition, type AssetStatus } from '@cfl/assets-shared';

import {
  Alert,
  AlertDescription,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Textarea,
} from '@/components/cargofl-ui';
import { ApiError } from '@/lib/api';
import { useUpdateAsset, type AssetDetail } from '@/lib/assets-api';

/**
 * Take an asset out of service.
 *
 * Its own dialog rather than a status dropdown buried in the edit form, because this
 * is the one status change that is close to irreversible - nothing ever leaves
 * DISPOSED - and it deserves to be a deliberate action with the consequence spelled
 * out, not a select somebody tabs past while fixing a typo in a model number.
 *
 * Which outcomes are offered is computed from the lifecycle rules rather than
 * hardcoded, so this list can never drift from what the API will actually accept.
 */

interface Outcome {
  status: AssetStatus;
  title: string;
  blurb: string;
  /** True for the ones that cannot be undone. */
  terminal?: boolean;
}

const OUTCOMES: Outcome[] = [
  {
    status: 'DAMAGED',
    title: 'Damaged',
    blurb:
      'Broken but still ours. Held back from the pool so it cannot be issued by accident, and it can still go for repair.',
  },
  {
    status: 'LOST',
    title: 'Lost',
    blurb: 'Cannot be found. Kept on the register so it still shows in an audit.',
  },
  {
    status: 'RETIRED',
    title: 'Retired',
    blurb:
      'End of its working life, still in the building. The normal end for a laptop that is too old to issue.',
  },
  {
    status: 'DISPOSED',
    title: 'Disposed of',
    blurb: 'Scrapped, sold, or gone to the e-waste vendor. This cannot be undone.',
    terminal: true,
  },
];

export function RetireAssetDialog({
  asset,
  open,
  onOpenChange,
}: {
  asset: AssetDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const update = useUpdateAsset(asset.id);
  const [choice, setChoice] = React.useState<AssetStatus | null>(null);
  const [note, setNote] = React.useState('');

  React.useEffect(() => {
    if (open) {
      setChoice(null);
      setNote('');
    }
  }, [open]);

  const stillHeld = asset.holder !== null;

  // Computed from the shared rule, not a hardcoded list - so an outcome is offered
  // only where the API would accept it, and the two cannot drift apart.
  const available = OUTCOMES.filter(
    (outcome) => outcome.status !== asset.status && canTransition(asset.status, outcome.status),
  );

  const submit = async () => {
    if (!choice) return;
    try {
      await update.mutateAsync({
        status: choice,
        // Appended, never replacing: the reason something was scrapped is worth more
        // than whatever was in the notes field, and losing the latter to keep the
        // former is a false choice.
        notes: note.trim()
          ? `${asset.notes ? `${asset.notes}\n` : ''}${ASSET_STATUS_LABELS[choice]}: ${note.trim()}`
          : undefined,
      });
      toast.success(`${asset.code} marked ${ASSET_STATUS_LABELS[choice].toLowerCase()}.`);
      onOpenChange(false);
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'That could not be recorded.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Take {asset.code} out of service</DialogTitle>
          <DialogDescription>
            {asset.name}
            {asset.serial ? ` · ${asset.serial}` : ''}
          </DialogDescription>
        </DialogHeader>

        {stillHeld ? (
          // The rule the API enforces, said before they pick rather than after they
          // submit: an asset cannot be written off while somebody still has it, or the
          // register says it is gone while it is in a bag.
          <Alert className="border-warning/40 bg-warning/5">
            <AlertDescription className="text-sm">
              <span className="font-medium">{asset.holder?.name} still has this.</span> Record
              its return first — otherwise the register would say it is gone while somebody
              is still carrying it.
            </AlertDescription>
          </Alert>
        ) : available.length === 0 ? (
          <Alert>
            <AlertDescription className="text-sm">
              This asset is already {ASSET_STATUS_LABELS[asset.status].toLowerCase()} and
              cannot be changed further.
            </AlertDescription>
          </Alert>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              {available.map((outcome) => (
                <button
                  key={outcome.status}
                  type="button"
                  onClick={() => setChoice(outcome.status)}
                  className={`w-full rounded-md border p-3 text-left transition-colors ${
                    choice === outcome.status
                      ? 'border-primary bg-accent'
                      : 'hover:bg-muted/50'
                  }`}
                >
                  <div className="flex items-center gap-2 text-sm font-medium">
                    {outcome.title}
                    {outcome.terminal && (
                      <span className="rounded-full bg-destructive/10 px-1.5 py-0.5 text-[10px] font-semibold text-destructive">
                        permanent
                      </span>
                    )}
                  </div>
                  <div className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
                    {outcome.blurb}
                  </div>
                </button>
              ))}
            </div>

            <div className="space-y-1.5">
              <Label>Why? (optional)</Label>
              <Textarea
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Screen cracked beyond economical repair. Quoted more than a replacement."
              />
              <p className="text-[11px] text-muted-foreground">
                Added to the asset’s notes and recorded in the audit log.
              </p>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant={choice === 'DISPOSED' ? 'destructive' : 'default'}
            disabled={!choice || stillHeld || update.isPending}
            onClick={() => void submit()}
          >
            {update.isPending
              ? 'Saving…'
              : choice
                ? `Mark ${ASSET_STATUS_LABELS[choice].toLowerCase()}`
                : 'Choose an outcome'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
